import { useState, useEffect, useRef, useCallback } from "react";

const CHAT_API_URL = "https://chat.digitrand.com";
const WS_URL = CHAT_API_URL.replace("http://", "ws://").replace(
  "https://",
  "wss://"
);

export const useSocketManager = ({ currentUser, activeUserId }) => {
  const [employees, setEmployees] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [conversations, setConversations] = useState({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [socketId, setSocketId] = useState(null);

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef({});
  const messageQueueRef = useRef([]);
  const isConnectingRef = useRef(false);
  const connectionAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  // ✅ FIX: Proper empty conversation structure
  const createEmptyConversation = useCallback(
    () => ({
      messages: [],
      typing: false,
      unread: 0,
      page: 1,
      hasMore: true,
      loaded: false,
      isLoading: false,
    }),
    []
  );

  const sendWsMessage = useCallback((type, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        type,
        payload,
        timestamp: new Date().toISOString(),
      });
      console.log(`📤 Sending ${type}:`, payload);
      wsRef.current.send(message);
      return true;
    }
    console.warn(`⚠️ WebSocket not ready for ${type}`);
    messageQueueRef.current.push({ type, payload });
    return false;
  }, []);

  const processMessageQueue = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const queue = [...messageQueueRef.current];
    messageQueueRef.current = [];
    queue.forEach(({ type, payload }) => sendWsMessage(type, payload));
  }, [sendWsMessage]);

  const initWebSocket = useCallback(() => {
    if (!currentUser?.id || isConnectingRef.current) return;

    console.log("🔌 Initializing WebSocket...");
    isConnectingRef.current = true;
    connectionAttemptsRef.current += 1;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setSocketConnected(true);
        setConnectionError(null);
        isConnectingRef.current = false;
        connectionAttemptsRef.current = 0;

        setTimeout(() => processMessageQueue(), 100);

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: "ping",
                timestamp: new Date().toISOString(),
              })
            );
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`📩 ${data.type}:`, data);

          switch (data.type) {
            case "connection_ack":
              setSocketId(data.socketId);
              setTimeout(() => {
                if (currentUser?.id && ws.readyState === WebSocket.OPEN) {
                  sendWsMessage("login", {
                    id: currentUser.id,
                    name: currentUser.name || "User",
                    email: currentUser.email || "",
                    photo: currentUser.photo?.trim() || null,
                    department: currentUser.department || "",
                    designation: currentUser.designation || "",
                    employee_code: currentUser.employee_code || "",
                  });
                }
              }, 500);
              break;

            case "login_success":
              console.log("✅ Login successful:", data.userId);
              setTimeout(() => sendWsMessage("get_online_users"), 1000);
              break;

            case "online_users":
            case "online_users_update":
              setOnlineIds((data.users || []).map((u) => u.userId));
              break;

            case "new_message":
              handleReceiveMessage(data);
              break;

            case "message_sent":
              handleMessageSentConfirmation(data);
              break;

            case "message_delivered":
              handleMessageDelivered(data);
              break;

            case "message_read":
              handleMessageRead(data);
              break;

            case "typing_indicator":
              handleTypingEvent(data);
              break;

            case "message_history":
              handleMessageHistory(data); // ✅ FIX: Will receive otherUserId now
              break;

            case "clear_chat_confirmation":
              handleClearChatConfirmation(data);
              break;

            case "pong":
              break;

            case "error":
              console.error("❌ Server error:", data.errorMessage);
              setConnectionError(data.errorMessage);
              break;
          }
        } catch (error) {
          console.error("❌ Parse error:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("🔴 WebSocket error:", error);
        setConnectionError("Connection error");
        setSocketConnected(false);
      };

      ws.onclose = (event) => {
        console.log("🔌 WebSocket closed:", event.code);
        setSocketConnected(false);
        isConnectingRef.current = false;

        if (connectionAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            initWebSocket();
          }, 3000);
        } else {
          setConnectionError("Cannot connect. Please refresh.");
        }
      };
    } catch (error) {
      console.error("❌ WS creation error:", error);
      setConnectionError("Connection failed");
      isConnectingRef.current = false;
    }
  }, [currentUser, processMessageQueue, sendWsMessage]);

  // ✅ FIX: Handle message history with otherUserId
  const handleMessageHistory = useCallback(
    (data) => {
      console.log("📜 Received message history:", data);

      if (!data?.messages || !data?.otherUserId) {
        console.error("❌ Missing data in message_history:", data);
        return;
      }

      const { messages, pagination, otherUserId } = data;

      setConversations((prev) => {
        const prevConv = prev[otherUserId] || createEmptyConversation();
        const page = pagination?.currentPage || 1;

        // Normalize messages
        const normalized = messages.map((msg) => ({
          _id: msg.messageId || `msg_${Date.now()}`,
          messageId: msg.messageId,
          senderId: msg.senderId,
          receiverId: msg.receiverId,
          message: msg.content || "",
          type: msg.type || "text",
          fileUrl: msg.attachment?.url || null,
          fileName: msg.attachment?.fileName || null,
          fileType: msg.attachment?.fileType || null,
          createdAt: msg.sentAt || msg.createdAt || new Date().toISOString(),
          status: msg.status || "sent",
          timestamp: msg.sentAt || msg.createdAt || new Date().toISOString(),
        }));

        // Merge based on page
        let merged;
        if (page === 1) {
          merged = normalized;
        } else {
          // Remove duplicates
          const existingIds = new Set(
            prevConv.messages.map((m) => m.messageId)
          );
          const newMessages = normalized.filter(
            (msg) => !existingIds.has(msg.messageId)
          );
          merged = [...prevConv.messages, ...newMessages];
        }

        // Sort chronologically
        merged.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        return {
          ...prev,
          [otherUserId]: {
            ...prevConv,
            messages: merged,
            page,
            hasMore: pagination?.hasNext || false,
            loaded: true,
            isLoading: false,
          },
        };
      });
    },
    [createEmptyConversation]
  );

  const handleReceiveMessage = useCallback(
    (data) => {
      if (!data.message || !currentUser?.id) return;

      const message = data.message;
      const otherId =
        message.senderId === currentUser.id
          ? message.receiverId
          : message.senderId;

      const normalized = {
        _id: message.messageId,
        messageId: message.messageId,
        senderId: message.senderId,
        receiverId: message.receiverId,
        message: message.content || "",
        type: message.type || "text",
        fileUrl: message.attachment?.url || null,
        fileName: message.attachment?.fileName || null,
        fileType: message.attachment?.fileType || null,
        createdAt: message.sentAt || new Date().toISOString(),
        status: message.status || "sent",
      };

      setConversations((prev) => {
        const conv = prev[otherId] || createEmptyConversation();

        // Avoid duplicates
        if (conv.messages.some((m) => m.messageId === message.messageId)) {
          return prev;
        }

        return {
          ...prev,
          [otherId]: {
            ...conv,
            messages: [...conv.messages, normalized],
            unread: activeUserId === otherId ? 0 : conv.unread + 1,
          },
        };
      });

      // Send receipts
      if (message.senderId !== currentUser.id && message.messageId) {
        setTimeout(() => {
          sendWsMessage("delivery_receipt", { messageId: message.messageId });
        }, 300);
      }

      if (
        activeUserId === otherId &&
        message.senderId !== currentUser.id &&
        message.messageId
      ) {
        sendWsMessage("read_receipt", { messageId: message.messageId });
      }
    },
    [currentUser, activeUserId, createEmptyConversation, sendWsMessage]
  );
  const handleTypingEvent = useCallback(
    (data) => {
      if (!data?.senderId) return;

      console.log(
        "✏️ [INCOMING] Received typing from:",
        data.senderId,
        "typing:",
        data.isTyping
      );

      // ✅ IMPORTANT: Only update if typing is from OTHER user (not self)
      if (data.senderId === currentUser?.id) {
        console.log("⚠️ Ignoring self typing event (from current user)");
        return;
      }

      // ✅ Update the conversation of the user who is typing
      setConversations((prev) => {
        const conv = prev[data.senderId] || createEmptyConversation();
        return {
          ...prev,
          [data.senderId]: {
            ...conv,
            typing: !!data.isTyping,
          },
        };
      });
    },
    [createEmptyConversation, currentUser?.id]
  ); // ✅ Added currentUser dependency

  const handleMessageSentConfirmation = useCallback((data) => {
    if (!data?.messageId) return;

    setConversations((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((uid) => {
        updated[uid].messages = updated[uid].messages.map((m) => {
          if (m._id?.startsWith("temp_")) {
            return {
              ...m,
              _id: data.messageId,
              messageId: data.messageId,
              status: "sent",
            };
          }
          return m;
        });
      });
      return updated;
    });
  }, []);

  const handleMessageDelivered = useCallback((data) => {
    if (data?.messageId) {
      setConversations((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((uid) => {
          updated[uid].messages = updated[uid].messages.map((m) =>
            m.messageId === data.messageId ? { ...m, status: "delivered" } : m
          );
        });
        return updated;
      });
    }
  }, []);

  const handleMessageRead = useCallback(
    (data) => {
      console.log("✅ [READ RECEIPT] Event received:", {
        messageId: data.messageId,
        readerId: data.readerId,
        currentUserId: currentUser?.id,
      });

      if (!data?.messageId || !currentUser?.id) return;

      // Debug: Check what messages we have
      console.log("🔍 Current conversations:", Object.keys(conversations));

      // Update ALL conversations where this message exists
      setConversations((prev) => {
        const updated = { ...prev };
        let found = false;

        Object.keys(updated).forEach((userId) => {
          updated[userId].messages = updated[userId].messages.map((msg) => {
            // Match by messageId AND check if current user sent this message
            if (
              msg.messageId === data.messageId &&
              msg.senderId === currentUser.id
            ) {
              console.log(
                `📨 [UPDATE] Setting message ${data.messageId} to 'read' in chat with ${userId}`
              );
              console.log(`🔎 Message details:`, {
                currentSender: msg.senderId,
                currentUser: currentUser.id,
                status: msg.status,
              });
              found = true;
              return { ...msg, status: "read" };
            }
            return msg;
          });
        });

        if (!found) {
          console.warn(
            "⚠️ [READ RECEIPT] Message not found in any conversation:",
            data.messageId
          );
          console.log(
            "📋 Available messages:",
            Object.keys(updated).map((uid) => ({
              userId: uid,
              messages: updated[uid].messages.map((m) => ({
                id: m.messageId,
                sender: m.senderId,
                status: m.status,
              })),
            }))
          );
        }

        return updated;
      });
    },
    [currentUser?.id, conversations]
  ); // ✅ Add conversations dependency

  const handleClearChatConfirmation = useCallback(
    (data) => {
      const { otherUserId } = data;
      setConversations((prev) => {
        const updated = { ...prev };
        if (updated[otherUserId]) {
          updated[otherUserId] = createEmptyConversation();
        }
        return updated;
      });
    },
    [createEmptyConversation]
  );

  useEffect(() => {
    if (!currentUser?.id) return;

    initWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setSocketConnected(false);
      setSocketId(null);
    };
  }, [currentUser?.id, initWebSocket]);

  const sendMessage = useCallback(
    (otherUserId, text, type = "text", fileUrl = null) => {
      if ((!text && !fileUrl) || !currentUser?.id || !socketConnected) {
        console.error("Cannot send message");
        return;
      }

      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const messageData = {
        _id: tempId,
        senderId: currentUser.id,
        receiverId: otherUserId,
        message: text || "",
        type: fileUrl ? type : "text",
        fileUrl,
        createdAt: new Date().toISOString(),
        status: "sending",
      };

      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: [...conv.messages, messageData],
          },
        };
      });

      const payload = {
        receiverId: otherUserId,
        content: text || "",
        messageType: fileUrl ? type : "text",
      };

      if (fileUrl) {
        payload.attachment = {
          url: fileUrl,
          fileName: "file",
          fileType: "application/octet-stream",
          fileSize: 0,
        };
      }

      const sent = sendWsMessage("send_message", payload);
      if (!sent) {
        setTimeout(() => {
          setConversations((prev) => {
            const conv = prev[otherUserId];
            if (!conv) return prev;
            return {
              ...prev,
              [otherUserId]: {
                ...conv,
                messages: conv.messages.map((m) =>
                  m._id === tempId ? { ...m, status: "error" } : m
                ),
              },
            };
          });
        }, 1000);
      }

      if (typingTimeoutRef.current[otherUserId]) {
        clearTimeout(typingTimeoutRef.current[otherUserId]);
        delete typingTimeoutRef.current[otherUserId];
      }
      sendWsMessage("typing", { receiverId: otherUserId, isTyping: false });
    },
    [currentUser, socketConnected, sendWsMessage, createEmptyConversation]
  );

  const handleTyping = useCallback(
    (otherUserId, isTyping) => {
      if (!socketConnected) return;

      console.log(
        "✏️ [OUTGOING] Sending typing to:",
        otherUserId,
        "isTyping:",
        isTyping
      );

      // ✅ Only send to server, DO NOT update local UI
      // Server will send back typing_indicator event to the receiver
      sendWsMessage("typing", { receiverId: otherUserId, isTyping });

      // ❌ REMOVED: Local UI update (causes self-typing display)
      // setConversations((prev) => {
      //   const conv = prev[otherUserId] || createEmptyConversation();
      //   return {
      //     ...prev,
      //     [otherUserId]: { ...conv, typing: isTyping },
      //   };
      // });

      // Clear previous timeout
      if (typingTimeoutRef.current[otherUserId]) {
        clearTimeout(typingTimeoutRef.current[otherUserId]);
      }

      // Auto-stop typing after 2 seconds
      if (isTyping) {
        typingTimeoutRef.current[otherUserId] = setTimeout(() => {
          console.log("✏️ [AUTO-STOP] Auto-stop typing for:", otherUserId);
          sendWsMessage("typing", { receiverId: otherUserId, isTyping: false });

          // ❌ REMOVED: Local UI update for stop
          // setConversations((prev) => {
          //   const conv = prev[otherUserId];
          //   if (!conv) return prev;
          //   return {
          //     ...prev,
          //     [otherUserId]: { ...conv, typing: false },
          //   };
          // });

          delete typingTimeoutRef.current[otherUserId];
        }, 2000);
      }
    },
    handleTypingEvent[(socketConnected, sendWsMessage)] // ✅ Removed createEmptyConversation dependency
  );

  const handleFileUpload = useCallback(
    async (otherUserId, file) => {
      if (!file || !currentUser?.id) return;

      const tempId = `file_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const type = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
        ? "video"
        : file.type.startsWith("audio/")
        ? "audio"
        : "document";

      setUploadingFiles((prev) => ({ ...prev, [tempId]: file.name }));
      setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

      const optimisticMsg = {
        _id: tempId,
        senderId: currentUser.id,
        receiverId: otherUserId,
        message: file.name,
        type,
        fileUrl: null,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        createdAt: new Date().toISOString(),
        status: "uploading",
      };

      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: [...conv.messages, optimisticMsg],
          },
        };
      });

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${CHAT_API_URL}/upload`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

        const result = await response.json();

        if (result.success) {
          let fullUrl = result.file.url;
          if (fullUrl && !fullUrl.startsWith("http")) {
            fullUrl = fullUrl.startsWith("/")
              ? `${CHAT_API_URL}${fullUrl}`
              : `${CHAT_API_URL}/${fullUrl}`;
          }

          setConversations((prev) => {
            const conv = prev[otherUserId];
            if (!conv) return prev;
            return {
              ...prev,
              [otherUserId]: {
                ...conv,
                messages: conv.messages.map((m) =>
                  m._id === tempId
                    ? {
                        ...m,
                        fileUrl: fullUrl,
                        status: "sent",
                        messageId: tempId,
                      }
                    : m
                ),
              },
            };
          });

          sendWsMessage("send_message", {
            receiverId: otherUserId,
            content: file.name,
            messageType: type,
            attachment: {
              url: fullUrl,
              fileName: result.file.fileName,
              fileType: result.file.fileType,
              fileSize: result.file.fileSize,
            },
          });
        }
      } catch (error) {
        console.error("❌ Upload error:", error);
        setConversations((prev) => {
          const conv = prev[otherUserId];
          if (!conv) return prev;
          return {
            ...prev,
            [otherUserId]: {
              ...conv,
              messages: conv.messages.map((m) =>
                m._id === tempId ? { ...m, status: "error" } : m
              ),
            },
          };
        });
      } finally {
        setTimeout(() => {
          setUploadingFiles((prev) => {
            const newState = { ...prev };
            delete newState[tempId];
            return newState;
          });
          setUploadProgress((prev) => {
            const newState = { ...prev };
            delete newState[tempId];
            return newState;
          });
        }, 2000);
      }
    },
    [currentUser, sendWsMessage, createEmptyConversation]
  );

  const loadHistory = useCallback(
    (otherUserId, page = 1) => {
      if (!currentUser?.id || !otherUserId || !socketConnected) return;

      console.log("📜 Loading history for:", otherUserId, "page:", page);

      // Set loading state
      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            isLoading: true,
          },
        };
      });

      sendWsMessage("get_message_history", {
        otherUserId,
        page,
        limit: 50,
      });
    },
    [currentUser, socketConnected, sendWsMessage, createEmptyConversation]
  );

  const getOnlineUsers = useCallback(() => {
    if (socketConnected) {
      sendWsMessage("get_online_users");
    }
  }, [socketConnected, sendWsMessage]);

  const markAllAsRead = useCallback(
    (otherUserId) => {
      if (socketConnected) {
        sendWsMessage("mark_all_read", { otherUserId });
      }

      setConversations((prev) => {
        const conv = prev[otherUserId];
        if (!conv) return prev;
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: conv.messages.map((m) =>
              m.status === "delivered" ? { ...m, status: "read" } : m
            ),
            unread: 0,
          },
        };
      });
    },
    [socketConnected, sendWsMessage]
  );

  const clearChat = useCallback(
    (otherUserId) => {
      if (socketConnected) {
        sendWsMessage("clear_chat", { otherUserId });
      }

      setConversations((prev) => {
        const updated = { ...prev };
        if (updated[otherUserId]) {
          updated[otherUserId] = createEmptyConversation();
        }
        return updated;
      });
    },
    [socketConnected, sendWsMessage, createEmptyConversation]
  );

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    connectionAttemptsRef.current = 0;
    initWebSocket();
  }, [initWebSocket]);

  return {
    employees,
    setEmployees,
    onlineIds,
    conversations,
    setConversations,
    loadingEmployees,
    setLoadingEmployees,
    socketConnected,
    connectionError,
    uploadingFiles,
    uploadProgress,
    socketId,

    sendMessage,
    handleTyping,
    handleFileUpload,
    updateMessageStatus: (messageId, newStatus) => {
      setConversations((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((uid) => {
          updated[uid].messages = updated[uid].messages.map((msg) =>
            msg.messageId === messageId ? { ...msg, status: newStatus } : msg
          );
        });
        return updated;
      });
    },
    loadHistory,
    getOnlineUsers,
    markAllAsRead,
    clearChat,
    reconnect,
  };
};
